import { cloudAuthDesktopV2 } from "./cloudAuthDesktopV2";

export interface AdminFeedbackListItem {
  id: string;
  email: string | null;
  feedbackType: string;
  message: string;
  appArea: string | null;
  pagePath: string | null;
  status: string;
  priority: string;
  topic: { id: string; title: string } | null;
  githubIssueUrl: string | null;
  githubIssueNumber: number | null;
  prUrl: string | null;
  attachments: Array<{ id: string; url: string; source: string }>;
  commentCount: number;
  createdAt: string;
}

export interface AdminFeedbackDetail extends AdminFeedbackListItem {
  rating: number | null;
  metadata: unknown;
  internalNotes: string | null;
  prNumber: number | null;
  comments: Array<{
    id: string;
    authorType: string;
    agentName: string | null;
    body: string;
    createdAt: string;
    author: { id: string; name: string | null; email: string } | null;
  }>;
  statusEvents: Array<{
    id: string;
    fromStatus: string | null;
    toStatus: string;
    actor: string;
    note: string | null;
    createdAt: string;
  }>;
}

/** True when the paired cloud user is a sys_admin (admin API returns 200). */
export async function checkSysAdmin(): Promise<boolean> {
  if (!cloudAuthDesktopV2.isSignedIn()) return false;
  try {
    const { status } = await cloudAuthDesktopV2.authorizedJson<unknown>("/api/admin/feedback?limit=1");
    return status === 200;
  } catch {
    return false;
  }
}

export async function listAdminFeedback(params?: {
  status?: string;
  cursor?: string;
}): Promise<{ items: AdminFeedbackListItem[]; nextCursor: string | null }> {
  const search = new URLSearchParams();
  if (params?.status) search.set("status", params.status);
  if (params?.cursor) search.set("cursor", params.cursor);
  const query = search.toString();
  const { status, body } = await cloudAuthDesktopV2.authorizedJson<{
    items?: AdminFeedbackListItem[];
    nextCursor?: string | null;
    error?: string;
  }>(`/api/admin/feedback${query ? `?${query}` : ""}`);
  if (status !== 200) throw new Error(body.error || `Feedback list failed (${status})`);
  return { items: body.items ?? [], nextCursor: body.nextCursor ?? null };
}

export async function getAdminFeedback(feedbackId: string): Promise<AdminFeedbackDetail> {
  const { status, body } = await cloudAuthDesktopV2.authorizedJson<{
    feedback?: AdminFeedbackDetail;
    error?: string;
  }>(`/api/admin/feedback/${encodeURIComponent(feedbackId)}`);
  if (status !== 200 || !body.feedback) {
    throw new Error(body.error || `Feedback detail failed (${status})`);
  }
  return body.feedback;
}

export async function postAdminFeedbackComment(feedbackId: string, text: string): Promise<void> {
  const { status, body } = await cloudAuthDesktopV2.authorizedJson<{ error?: string }>(
    `/api/admin/feedback/${encodeURIComponent(feedbackId)}/comments`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ body: text }),
    }
  );
  if (status !== 200) throw new Error(body.error || `Comment failed (${status})`);
}
